import {DEFAULT_PAGE_SIZE} from 'hooks/tables';
import {AppState} from '../index';
import {
  MarketActivityEnum,
  MarketActivityItem,
  MarketFilter,
  MarketPagination,
  MarketRoleEnum,
  SummaryStat,
} from './types';

interface State {
  isLoading: boolean;
  summary: SummaryStat;
  activityList: MarketActivityItem[];
  filter: MarketFilter;
  totalCount: number;
  pagination: MarketPagination;
}

const initialState: State = {
  isLoading: false,
  summary: {
    offersAdded: 0,
    priceChanged: 0,
    dealsClosed: 0,
  },
  activityList: [],
  filter: {
    days: 1,
    activity: MarketActivityEnum.All,
    role: MarketRoleEnum.All,
  },
  totalCount: 0,
  pagination: {
    perPage: DEFAULT_PAGE_SIZE,
    currentPage: 0,
  }
};

const SET_LOADING = '@market-activity/loading';
const UPDATE_ACTIVITY = '@market-activity/update';
const UPDATE_ACTIVITY_APPEND = '@market-activity/append';
const UPDATE_SUMMARY = '@market-activity/update-summary';
const UPDATE_FILTER = '@market-activity/update-filter';
const SET_PAGINATION = '@market-activity/pagination';
const SET_TOTAL_COUNT = '@market-activity/total-count';

interface SetLoading {
  type: typeof SET_LOADING;
  data: boolean;
}

interface SetPagination {
  type: typeof SET_PAGINATION;
  data: MarketPagination;
}

interface SetTotalCount {
  type: typeof SET_TOTAL_COUNT;
  data: number;
}

interface UpdateSummary {
  type: typeof UPDATE_SUMMARY;
  data: SummaryStat;
}

interface UpdateActivity {
  type: typeof UPDATE_ACTIVITY;
  data: MarketActivityItem[];
}

interface UpdateActivityAppend {
  type: typeof UPDATE_ACTIVITY_APPEND;
  data: MarketActivityItem[];
}

interface UpdateFilter {
  type: typeof UPDATE_FILTER;
  data: MarketFilter;
}

export type MarketActivityAction =
  SetLoading
  | UpdateSummary
  | UpdateActivity
  | UpdateActivityAppend
  | UpdateFilter
  | SetPagination
  | SetTotalCount;

export const marketActivityReducer = (
  state: State = initialState,
  action: MarketActivityAction,
) => {
  switch (action.type) {
    case SET_LOADING:
      return {
        ...state,
        isLoading: action.data,
      };

    case SET_PAGINATION:
      return {
        ...state,
        pagination: action.data,
      };

    case SET_TOTAL_COUNT:
      return {
        ...state,
        totalCount: action.data,
      };

    case UPDATE_SUMMARY:
      return {
        ...state,
        summary: action.data,
      };

    case UPDATE_ACTIVITY:
      return {
        ...state,
        activityList: action.data,
      };

    case UPDATE_ACTIVITY_APPEND:
      return {
        ...state,
        activityList: [
          ...state.activityList,
          ...action.data
        ],
      };

    case UPDATE_FILTER:
      return {
        ...state,
        filter: action.data,
      };

    default:
      return state;
  }
};

export const setMarketListLoading = (status: boolean) => {
  return {
    type: SET_LOADING,
    data: status,
  };
};

export const isMarketListLoading = (state: AppState) =>
  state.marketActivity.isLoading;

export const setMarketPagination = (pagination: MarketPagination) => {
  return {
    type: SET_PAGINATION,
    data: pagination,
  };
};

export const getMarketPagination = (state: AppState) => {
  return state.marketActivity.pagination;
};

export const setMarketTotalCount = (count: number) => {
  return {
    type: SET_TOTAL_COUNT,
    data: count,
  };
};

export const getMarketTotalCount = (state: AppState) =>
  state.marketActivity.totalCount;

export const setSummaryStats = (stats: SummaryStat) => {
  return {
    type: UPDATE_SUMMARY,
    data: stats,
  };
};

export const getSummaryStat = (state: AppState) =>
  state.marketActivity.summary;

export const setMarketActivityList = (list: MarketActivityItem[]) => {
  return {
    type: UPDATE_ACTIVITY,
    data: list,
  };
};

export const setMarketActivityListAppend = (list: MarketActivityItem[]) => {
  return {
    type: UPDATE_ACTIVITY_APPEND,
    data: list,
  };
};

export const getMarketActivityList = (state: AppState) =>
  state.marketActivity.activityList;

export const setMarketActivityFilter = (filter: MarketFilter) => {
  return {
    type: UPDATE_FILTER,
    data: filter,
  };
};

export const getMarketActivityFilter = (state: AppState) =>
  state.marketActivity.filter;
